// Zoolife Auth API Service
// Based on Session-Based Authentication API Documentation
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { getUserData, isAuthenticated, clearAuthData, handleApiResponse } from './userDataService';
import { AUTH_API_URL, GENERAL_API_URL, CAPTCHA_API_URL } from '../config/api';

const API_BASE_URL = AUTH_API_URL;

// Helper function to get session ID
export const getSessionId = async () => {
  try {
    const sessionId = await AsyncStorage.getItem('session_id');
    // console.log('Retrieved session ID:', sessionId ? 'Present' : 'Missing', sessionId);
    return sessionId;
  } catch (error) {
    console.error('Error getting session ID:', error);
    return null;
  }
};

// Debug function to check all AsyncStorage keys
export const debugAsyncStorage = async () => {
  try {
    const keys = await AsyncStorage.getAllKeys();
    // console.log('All AsyncStorage keys:', keys);
    
    const values = await AsyncStorage.multiGet(keys);
    const storage = {};
    values.forEach(([key, value]) => {
      storage[key] = value;
    });
    // console.log('AsyncStorage contents:', storage);
    return storage;
  } catch (error) {
    console.error('Error debugging AsyncStorage:', error);
    return {};
  }
};

// Test function to verify login flow
export const testLoginFlow = async (credentials) => {
  console.log('=== TESTING LOGIN FLOW ===');
  
  // Clear existing data first
  console.log('1. Clearing existing session data...');
  await clearAuthData();
  await debugAsyncStorage();
  
  // Attempt login
  console.log('2. Attempting login...');
  try {
    const result = await authenticate(credentials);
    console.log('3. Login successful, checking stored data...');
    await debugAsyncStorage();
    
    // Test session retrieval
    console.log('4. Testing session retrieval...');
    const sessionId = await getSessionId();
    const rememberToken = await getRememberToken();
    // console.log('Retrieved session data:', {
    //   sessionId: sessionId ? 'Present' : 'Missing',
    //   rememberToken: rememberToken ? 'Present' : 'Missing',
    //   sessionIdValue: sessionId,
    //   rememberTokenValue: rememberToken
    // });
    
    return { success: true, result };
  } catch (error) {
    console.error('5. Login failed:', error);
    return { success: false, error: error.message };
  }
};

// Get User Profile (Session-based)
export const getUserProfile = async () => {
  try {
    const response = await makeAuthenticatedApiCall(`${API_BASE_URL}/getUserProfile`, {
      method: 'GET',
    });
    
    // Debug log to see the response structure
    console.log('getUserProfile response:', JSON.stringify(response, null, 2));
    
    // Update auth_token in localStorage if included in response
    console.log('trying to update auth_token');
    if (response.data && response.data.auth_token) {
      await AsyncStorage.setItem('auth_token', response.data.auth_token);
      console.log('Updated auth_token in localStorage');
    } else {
      console.log('No auth_token found in response');
    }
    
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to get user profile');
  }
};

// Get User Point History (Session-based)
export const getUserPointHistory = async () => {
  try {
    return await makeAuthenticatedApiCall(`${API_BASE_URL}/getUserPointHistory`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error.message || 'Failed to get user point history');
  }
};

// Test endpoint (Session-based)
export const testAuth = async () => {
  try {
    return await makeAuthenticatedApiCall(`${API_BASE_URL}/test`, {
      method: 'GET',
    });
  } catch (error) {
    throw new Error(error.message || 'Auth test failed');
  }
};

// Helper function to check if session is valid
export const isSessionValid = async () => {
  try {
    const sessionId = await getSessionId();
    if (!sessionId) {
      return false;
    }
    
    // Try to make a test call to verify session
    await testAuth();
    return true;
  } catch (error) {
    return false;
  }
};

// Helper function to get current session info
export const getSessionInfo = async () => {
  try {
    const sessionId = await getSessionId();
    const rememberToken = await getRememberToken();
    
    return {
      sessionId,
      rememberToken,
      hasSession: !!sessionId,
      hasRememberToken: !!rememberToken,
    };
  } catch (error) {
    return {
      sessionId: null,
      rememberToken: null,
      hasSession: false,
      hasRememberToken: false,
    };
  }
};

// Helper function to get remember token from storage
export const getRememberToken = async () => {
  try {
    const rememberToken = await AsyncStorage.getItem('remember_token');
    // console.log('getRememberToken result:', rememberToken ? 'Present' : 'Missing');
    return rememberToken;
  } catch (error) {
    console.error('Error getting remember token:', error);
    return null;
  }
};

// Helper function to store session data
export const storeSessionData = async (sessionId, rememberToken) => {
  try {
    // console.log('Storing session data:', {
    //   sessionId: sessionId ? 'Present' : 'Missing',
    //   rememberToken: rememberToken ? 'Present' : 'Missing',
    //   sessionIdValue: sessionId
    // });
    await AsyncStorage.multiSet([
      ['session_id', sessionId],
      ['remember_token', rememberToken || ''],
    ]);
    console.log('Session data stored successfully');
  } catch (error) {
    console.error('Error storing session data:', error);
  }
};

// Enhanced API call function with automatic session refresh
export const makeAuthenticatedApiCall = async (url, options = {}) => {
  const sessionId = await getSessionId();
  const rememberToken = await getRememberToken();
  
  // // Debug logging
  // console.log('makeAuthenticatedApiCall Debug:', {
  //   url,
  //   sessionId: sessionId ? 'Present' : 'Missing',
  //   rememberToken: rememberToken ? 'Present' : 'Missing',
  //   sessionIdValue: sessionId,
  //   rememberTokenValue: rememberToken
  // });
  
  // Add session headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (sessionId) {
    headers['X-Session-ID'] = sessionId;
    // console.log('Added X-Session-ID header:', sessionId);
  } else {
    console.warn('No session ID available for API call');
  }
  
  if (rememberToken) {
    headers['X-Remember-Token'] = rememberToken;
    // console.log('Added X-Remember-Token header');
  } else {
    console.warn('No remember token available for API call');
  }
  
  const requestOptions = {
    ...options,
    headers,
  };
  
  try {
    let response = await fetch(url, requestOptions);
    let responseData = await response.json();
    
    // Check for automatic session refresh
    if (responseData.new_session_id) {
      await storeSessionData(responseData.new_session_id, rememberToken);
    }
    
    // Handle session refresh on 401 error
    if (response.status === 401 && rememberToken) {
      try {
        const refreshResult = await refreshSession(rememberToken);
        if (refreshResult.status === 'success') {
          // Retry original request with new session
          headers['X-Session-ID'] = refreshResult.data.session_id;
          requestOptions.headers = headers;
          response = await fetch(url, requestOptions);
          responseData = await response.json();
        }
      } catch (refreshError) {
        // If refresh fails, clear auth data and throw error
        await clearAuthData();
        const sessionExpiredError = new Error('Session expired. Please login again.');
        sessionExpiredError.code = 401;
        throw sessionExpiredError;
      }
    }
    
    return await handleApiResponse(response, responseData);
  } catch (error) {
    if (error.code && error.code === 401) throw error;
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
    }
    throw error;
  }
};

// Session refresh function
export const refreshSession = async (rememberToken = null) => {
  try {
    const tokenToUse = rememberToken || await getRememberToken();
    
    if (!tokenToUse) {
      throw new Error('No remember token available');
    }
    
    const response = await fetch(`${API_BASE_URL}/refreshSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Remember-Token': tokenToUse,
      },
      body: JSON.stringify({ remember_token: tokenToUse }),
    });
    
    const result = await handleApiResponse(response);
    
    // Store new session data
    if (result.data && result.data.session_id) {
      await storeSessionData(result.data.session_id, tokenToUse);
    }
    
    return result;
  } catch (error) {
    throw new Error(error.message || 'Session refresh failed');
  }
};



// User Authentication
export const authenticate = async (credentials) => {
  try {
    console.log('Starting authentication process...');
    
    const response = await fetch(`${API_BASE_URL}/authenticate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        'email-or-telp': credentials['email-or-telp'] || credentials.email || credentials.emailOrTelp,
        'password': credentials.password,
        'captcha': credentials.captcha,
        'captcha-token': credentials.captchaToken || credentials['captcha-token'] || null,
      }),
    });
    
    const result = await handleApiResponse(response);
    
    // console.log('Authentication API Response:', {
    //   status: result.status,
    //   hasData: !!result.data,
    //   sessionId: result.data?.session_id ? 'Present' : 'Missing',
    //   rememberToken: result.data?.remember_token ? 'Present' : 'Missing',
    //   dataKeys: result.data ? Object.keys(result.data) : 'No data'
    // });
    
    // Store session data after successful authentication
    if (result.data && result.data.session_id) {
      console.log('Storing session data from login response...');
      await storeSessionData(result.data.session_id, result.data.remember_token);
      
      // Verify storage immediately
      const storedSessionId = await getSessionId();
      const storedRememberToken = await getRememberToken();
      // console.log('Post-login verification:', {
      //   storedSessionId: storedSessionId ? 'Present' : 'Missing',
      //   storedRememberToken: storedRememberToken ? 'Present' : 'Missing'
      // });
    } else {
      console.warn('No session_id found in authentication response!');
    }
    
    return result;
  } catch (error) {
    console.error('Authentication error:', error);
    // Handle network errors specifically
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
    }
    // Re-throw the error with the original message
    throw error;
  }
};

// Keep loginAdmin for backward compatibility
export const loginAdmin = authenticate;

// Send Reset Password
export const sendResetPassword = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/sendResetPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    return await handleApiResponse(response);
  } catch (error) {
    throw new Error(error.message || 'Failed to send reset email');
  }
};

// Verify Reset Token
export const verifyResetToken = async (resetCode) => {
  try {
    const response = await fetch(`${API_BASE_URL}/verifyResetToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reset_code: resetCode }),
    });
    
    return await handleApiResponse(response);
  } catch (error) {
    throw new Error(error.message || 'Token verification failed');
  }
};

// Save New Password
export const saveNewPassword = async (resetCode, password, passwordConfirm) => {
  try {
    const response = await fetch(`${API_BASE_URL}/saveNewPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reset_code: resetCode,
        password: password,
        password_confirm: passwordConfirm,
      }),
    });
    
    return await handleApiResponse(response);
  } catch (error) {
    throw new Error(error.message || 'Password update failed');
  }
};

// Logout
export const logout = async () => {
  try {
    // Use session-based authentication for logout
    const result = await makeAuthenticatedApiCall(`${API_BASE_URL}/logout`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    
    // Clear local storage after successful logout
    await clearAuthData();
    await AsyncStorage.multiRemove(['session_id', 'remember_token']);
    
    return result;
  } catch (error) {
    // Even if API call fails, clear local data
    await clearAuthData();
    await AsyncStorage.multiRemove(['session_id', 'remember_token']);
    // throw new Error(error.message || 'Logout failed');
    throw error;
  }
};

// Token-based login using external API
export const loginWithToken = async (token, backUrl = null) => {
  try {
    console.log('Starting token-based login with token:', token ? 'Present' : 'Missing');
    
    if (!token) {
      throw new Error('Token is required for token-based login');
    }

    // Call external API to validate token and get user data
    console.log('Calling external API to validate token...');
    
    let response;
    let result = null; // Initialize result with null to ensure it's always defined
    
    // Use a flag to track if the inner API call failed
    let apiCallFailed = false;
    let apiError = null;
    
    try {
      // Call external API with X-Api-Key header
      response = await fetch(`${GENERAL_API_URL}/v1/getdata`, {
        method: 'POST',
        headers: {
          'X-Api-Key': Constants.expoConfig?.extra?.API_KEY || '9f3c7a2d1b84e6f5c0a9d27e4b6f8a1c5d3e9b7a0c2f4e6d8a1b3c5f7e9d2',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'auth_token': token
        }),
      });

      result = await response.json();
      console.log('External API response:', { status: response.status, result });
      console.log('Result var set: ', result);

      if (!response.ok) {
        apiCallFailed = true;
        apiError = new Error(`External API error: ${response.status} - ${result?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      apiCallFailed = true;
      
      // Check if this is a CORS error
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        apiError = new Error('CORS error: API server tidak mengizinkan permintaan dari browser. Silakan hubungi administrator untuk menambahkan header CORS yang diperlukan.');
      }
      
      // Check if this is a JSON parsing error
      else if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
        apiError = new Error('Error parsing response dari server API eksternal.');
      }
      
      // If we get here, it means the API call failed completely
      else {
        apiError = new Error(`Koneksi gagal: ${error.message}`);
      }
    }
    
    // If the API call failed, return early with the error
    if (apiCallFailed) {
      throw apiError;
    }

    // If we reach here, it means the inner try-catch completed without throwing
    // and result should be properly assigned
    if (!result) {
      throw new Error('Tidak ada respons dari server API eksternal');
    }

    // Process the response and integrate with existing authentication system
    // The external API returns a different data format, so we need to handle it properly
    
    if (result && result.status === 'success') {
      console.log('External API response validated successfully');

      // Clear previous session before storing new — fixes SSO user-switch (e.g. User A → User B)
      await clearAuthData();

      // Extract user data from the correct locations in the API response
      // Your API response has credentials at the root level, not inside result.data
      const userData = {
        // Use data from result.data if available
        ...(result.data || {}),
        // Override with credentials from root level if they exist
        ...(result.email ? { email: result.email } : {}),
        ...(result.telp ? { telp: result.telp } : {}),
        ...(result.password ? { password: result.password } : {})
      };
      
      console.log('Processed user data for authentication:', userData);
      
      // Check if we have the required credentials for authentication
      // The credentials are at the root level: session_id and remember_token
      // Use userData since that's where we consolidated all the data
      const hasSessionCredentials = userData.session_id && userData.remember_token;
      
      console.log('Credential check debug:', {
        session_id: userData.session_id,
        remember_token: userData.remember_token,
        session_id_type: typeof userData.session_id,
        remember_token_type: typeof userData.remember_token,
        session_id_truthy: !!userData.session_id,
        remember_token_truthy: !!userData.remember_token,
        hasSessionCredentials: hasSessionCredentials
      });
      
      if (hasSessionCredentials) {
        console.log('Attempting authentication with external credentials');
        
        // Store the complete API response for reference
        await AsyncStorage.setItem('externalApiResponse', JSON.stringify(result));
        
        // Store user data for app usage (including the credentials)
        await AsyncStorage.setItem('userData', JSON.stringify(userData));
        
        // Use the session_id and remember_token from userData (where we consolidated all data)
        const sessionId = userData.session_id;
        const rememberToken = userData.remember_token;
        
        console.log('Using session data from API response:', {
          sessionId: sessionId ? 'Present' : 'Missing',
          rememberToken: rememberToken ? 'Present' : 'Missing',
          sessionIdValue: sessionId,
          rememberTokenValue: rememberToken
        });
        
        // Store session data using the values from the API response
        await storeSessionData(sessionId, rememberToken);
        
        // Store userToken like LoginScreen does for authentication state detection
        await AsyncStorage.setItem('userToken', rememberToken);
        
        console.log('Token-based login completed successfully with external credentials');
        return {
          success: true,
          data: userData,
          message: 'Login successful'
        };
      } else {
        console.warn('External API response does not contain complete credentials');
        console.log('Available credentials:', {
          hasSessionId: !!result.session_id,
          hasRememberToken: !!result.remember_token,
          session_id: result.session_id,
          remember_token: result.remember_token,
          hasEmail: !!result.email,
          hasTelp: !!result.telp,
          hasPassword: !!result.password
        });
        
        // Even without complete credentials, we can still store what we received
        if (result.data) {
          await AsyncStorage.setItem('userData', JSON.stringify(result.data));
        }
        
        // Create a minimal session using available data
        const sessionId = result.session_id || `ext_${Date.now()}`;
        const rememberToken = result.remember_token || '';
        
        await storeSessionData(sessionId, rememberToken);
        await AsyncStorage.setItem('userToken', rememberToken || 'authenticated');
        
        console.log('Token-based login completed with partial external data');
        return {
          success: true,
          data: result.data || {},
          message: 'Login successful (partial credentials)'
        };
      }
    } else {
      console.error('Token validation failed. Response:', result);
      throw new Error(result?.message || 'Token validation failed');
    }

  } catch (error) {
    console.error('Token-based login error:', error);
    
    // Handle specific error types
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        success: false,
        message: 'Koneksi internet bermasalah. Silakan periksa koneksi Anda.'
      };
    }
    
    return {
      success: false,
      message: error.message || 'Token-based login failed'
    };
  }
};

// Generate auto-login URL for external commerce site
export const generateAutoLoginUrl = async (redirectUrl = `${AUTH_API_URL.replace('/api/auth', '')}/commerce`) => {
  try {
    // Get remember token from AsyncStorage (as per documentation)
    let rememberToken = await getRememberToken();
    
    if (!rememberToken) {
      throw new Error('Remember token not found. Please login again.');
    }

    console.log('Generating auto-login link with remember token');

    // Helper function to attempt auto-login
    const attemptAutoLogin = async (token) => {
      const response = await fetch(`${API_BASE_URL}/autoLogin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Remember-Token': token,
        },
        credentials: 'include', // Include cookies to maintain session
        // No body required as per documentation
      });

      const result = await response.json();
      return { response, result };
    };

    // First attempt with current remember token
    let { response, result } = await attemptAutoLogin(rememberToken);
    
    // If we get a 401 (Unauthorized), try to refresh the session
    if (response.status === 401 && result.message?.includes('remember token')) {
      console.log('Remember token expired, attempting to refresh session...');
      
      try {
        // Attempt to refresh the session
        await refreshSession(rememberToken);
        
        // Get the potentially updated remember token
        const refreshedToken = await getRememberToken();
        
        if (refreshedToken) {
          console.log('Session refreshed, retrying auto-login...');
          // Retry auto-login with refreshed session
          const retryResult = await attemptAutoLogin(refreshedToken);
          response = retryResult.response;
          result = retryResult.result;
        }
      } catch (refreshError) {
        console.error('Session refresh failed:', refreshError);
        throw new Error('Session expired. Please login again.');
      }
    }
    
    if (!response.ok || result.code !== 200) {
      console.error('Auto-login link generation failed:', result);
      throw new Error(result.message || 'Auto-login failed');
    }
    
    console.log('Auto-login link generated:', result);

    // Extract the login_url from response as per documentation
    const autoLoginUrl = result.data?.login_url;
    
    if (!autoLoginUrl) {
      throw new Error('Login URL not found in response');
    }
    
    // Check for session isolation support (future-ready implementation)
    const webRememberToken = result.data?.web_remember_token;
    
    if (webRememberToken) {
      console.log('✅ Session isolation supported: Web session will use separate remember token');
      console.log('Mobile and web sessions are now independent');
      // Note: The web session will automatically use the new token
      // This ensures proper session isolation between platforms
    } else {
      console.warn('⚠️  Session isolation not yet implemented on backend');
      console.warn('Web and mobile sessions will share the same remember token');
      console.warn('See docs/api/session-isolation-requirements.md for implementation details');
    }
    
    console.log('Final auto-login URL:', autoLoginUrl);
    
    // CRITICAL SESSION ISOLATION ISSUE:
    // The current auto-login implementation has a session isolation problem:
    // 1. Mobile app uses its remember_token to generate auto-login URL
    // 2. Web session is created using the SAME remember_token
    // 3. If user logs out from web, it may invalidate the mobile app session
    // 4. Both sessions share the same authentication token
    
    // RECOMMENDED BACKEND MODIFICATION:
    // The /api/auth/autoLogin endpoint should return a NEW remember_token for web session:
    // {
    //   "code": 200,
    //   "status": "success",
    //   "data": {
    //     "login_url": "{API_BASE_URL}/auto-login/{token}",
    //     "user": {...},
    //     "session_id": "web_session_id",
    //     "web_remember_token": "new_separate_token_for_web_session"
    //   }
    // }
    
    // IMPLEMENTATION NOTES:
    // - Mobile app keeps its original remember_token
    // - Web session gets a new, independent remember_token
    // - Logging out from web won't affect mobile app session
    // - Each platform maintains separate authentication state
    
    console.warn('⚠️  SESSION ISOLATION WARNING:');
    console.warn('Current implementation shares remember_token between mobile and web.');
    console.warn('Logging out from web may break mobile app session.');
    console.warn('Backend should return separate web_remember_token for proper isolation.');
    
    return autoLoginUrl;
  } catch (error) {
    console.error('Error generating auto-login URL:', error);
    throw error;
  }
};

// Generate captcha
export const getCaptcha = async () => {
  try {
    console.log('Getting captcha...');
    
    const response = await fetch(`${CAPTCHA_API_URL}/generate?t=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'omit',
    });
    
    const result = await response.json();
    
    console.log('Captcha OK');
    
    return result;
  } catch (error) {
    console.error('Captcha error:', error);
    // Handle network errors specifically
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
    }
    // Re-throw the error with the original message
    throw error;
  }
};

// Check captcha (captcha_token required for cross-site domains)
export const checkCaptcha = async (captcha, captchaToken = '') => {
  try {
    console.log('Comparing captcha...');

    const formData = new FormData();
    formData.append('captcha', captcha);
    if (captchaToken) {
      formData.append('captcha_token', captchaToken);
    }
    
    const response = await fetch(`${CAPTCHA_API_URL}/check`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
      credentials: 'omit',
      body: formData,
    });
    
    const result = await response.json();
    
    console.log('Captcha check complete');
    
    return result;
  } catch (error) {
    console.error('Captcha error:', error);
    // Handle network errors specifically
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Koneksi internet bermasalah. Silakan periksa koneksi Anda.');
    }
    // Re-throw the error with the original message
    throw error;
  }
};